import { Component, Output, EventEmitter, ViewChild, ElementRef, OnDestroy, OnInit, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { PopoverModule } from 'primeng/popover';
import { ToastService } from '../../services/toast.service';
import { RepoService } from '../../services/repo.service';
import { SelectSTDMComponent } from '../../components/dialogue/select-stdm/select-stdm.component';
import { CreateRepoComponent } from '../../components/dialogue/create-repo/create-repo.component';
import { WorkflowService, WorkflowType } from '../../services/workflow.service';
import { AiService } from '../../services/ai/ai.service';
import {
  heroListBullet,
  heroPlus,
  heroXMark,
} from '@ng-icons/heroicons/outline';
import {
  phosphorGraph,
  phosphorListChecks,
  phosphorSealWarning,
} from '@ng-icons/phosphor-icons/regular';
import { heroArrowUpSolid, heroCheckCircleSolid, heroRocketLaunchSolid, heroStopSolid } from '@ng-icons/heroicons/solid';
import { heroChevronDown } from '@ng-icons/heroicons/outline';
import { filter, Subscription } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';

@Component({
  selector: 'app-textarea',
  imports: [
    FormsModule,
    NgIcon,
    PopoverModule,
    SelectSTDMComponent,
    CommonModule,
    CreateRepoComponent,
  ],
  templateUrl: './textarea.component.html',
  styleUrl: './textarea.component.css',
  providers: [
    provideIcons({
      heroListBullet,
      heroPlus,
      heroXMark,
      heroChevronDown,
      phosphorGraph,
      phosphorListChecks,
      heroRocketLaunchSolid,
      heroStopSolid,
      heroArrowUpSolid,
      heroCheckCircleSolid,
      phosphorSealWarning,
    }),
  ],
})
export class TextareaComponent implements OnDestroy, OnInit {
  private routerSubscription: Subscription;
  private typingInterval: any;
  private promptChangeTimeout: any;

  userInput: string = '';
  isTypingPrompt: boolean = false;
  createRepoVisible: boolean = false;
  workspace: boolean = false;

  // Animated placeholder
  @Input() enableTypingPlaceholder: boolean = true;
  animatedPlaceholder: string = '';
  private placeholderPrompts: string[] = [
    'What is the distribution of age, sex, and race?',
    'Show me adverse events by severity...',
    'Are there any missing baseline values?',
    'Compare treatment arms demographics...',
    'Which lab parameters are abnormal?',
  ];
  private currentPromptIndex: number = 0;
  private currentCharIndex: number = 0;
  private isDeleting: boolean = false;

  @ViewChild('messageTextarea') messageTextarea!: ElementRef<HTMLTextAreaElement>;
  @Output() sendMessage = new EventEmitter<string>();

  get workflowOptions() {
    return this.workflow.workflowOptions;
  }

  selectedWorkflow: WorkflowType | null = null;

  get currentStudy(): string | null {
    return this.repoService.repo()?.name || null;
  }

  get displayPlaceholder(): string {
    if (this.selectedWorkflow) {
      return this.selectedWorkflow.inpPlaceholder;
    }
    if (this.workspace || !this.enableTypingPlaceholder) {
      return 'Ask anything about your clinical data...';
    }
    // Always return the animated placeholder (even if empty for cursor blink effect)
    return this.animatedPlaceholder;
  }

  constructor(
    private toastService: ToastService,
    private repoService: RepoService,
    private workflow: WorkflowService,
    public aiService: AiService,
    private router: Router
  ) {
    this.workflow.currentWorkFlow$.subscribe((wf) => {
      this.selectedWorkflow = wf;
    });

    this.routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
        const page = event.url.split('/')[1];
        this.manageTextArea(page);
      });
  }

  ngOnInit() {
    this.startTypingAnimation();
  }

  ngOnDestroy() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    this.stopTypingAnimation();
  }

  private startTypingAnimation(): void {
    if (this.workspace || !this.enableTypingPlaceholder) return;

    const typeSpeed = 50;
    const deleteSpeed = 30;
    const pauseAfterTyping = 2000;
    const pauseAfterDeleting = 500;

    const animate = () => {
      const currentPrompt = this.placeholderPrompts[this.currentPromptIndex];

      if (!this.isDeleting) {
        // Typing
        if (this.currentCharIndex < currentPrompt.length) {
          this.animatedPlaceholder = currentPrompt.substring(0, this.currentCharIndex + 1);
          this.currentCharIndex++;
          this.typingInterval = setTimeout(animate, typeSpeed);
        } else {
          // Finished typing, pause then start deleting
          this.promptChangeTimeout = setTimeout(() => {
            this.isDeleting = true;
            animate();
          }, pauseAfterTyping);
        }
      } else {
        // Deleting
        if (this.currentCharIndex > 0) {
          this.animatedPlaceholder = currentPrompt.substring(0, this.currentCharIndex - 1);
          this.currentCharIndex--;
          this.typingInterval = setTimeout(animate, deleteSpeed);
        } else {
          // Finished deleting, move to next prompt
          this.isDeleting = false;
          this.currentPromptIndex = (this.currentPromptIndex + 1) % this.placeholderPrompts.length;
          this.promptChangeTimeout = setTimeout(animate, pauseAfterDeleting);
        }
      }
    };

    animate();
  }

  private stopTypingAnimation(): void {
    if (this.typingInterval) {
      clearTimeout(this.typingInterval);
    }
    if (this.promptChangeTimeout) {
      clearTimeout(this.promptChangeTimeout);
    }
  }

  autoResize(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';

    // Get the line height (default is usually around 20px)
    const lineHeight = parseInt(getComputedStyle(textarea).lineHeight) || 20;
    const minHeight = lineHeight * 2; // Height for 2 rows

    // Calculate new height based on scrollHeight
    const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), 100);
    // Apply the new height
    textarea.style.height = `${newHeight}px`;
    // Force a reflow to ensure the height is updated
    textarea.offsetHeight;
  }

  async handleSend(): Promise<void> {

    this.sendMessage.emit(this.userInput);
    this.userInput = ''; // Clear the input immediately

    // Reset textarea height to minimum
    if (this.messageTextarea) {
      const textarea = this.messageTextarea.nativeElement;
      textarea.style.height = 'auto';
    }
  }
  handleStop() {
    this.aiService.setAiIsWriting(false);
  }

  repoSelectionError(): void {
    this.toastService.showError(
      'Please select a Study before sending your message'
    );
    this.isTypingPrompt = false;
  }

  onWorkflowSubmit(workflow: WorkflowType): void {
    this.userInput = "Let's Start!!";
    this.workflow.setWorkflow(workflow);
  }

  updateWorkflow(workflow: WorkflowType | null): void {
    this.workflow.setWorkflow(workflow);
  }

  manageTextArea(page: string): void {
    if (page === 'workspace') {
      this.workspace = true;
    }else {
      this.workspace = false;
    }
  }
}
